import * as User                       from '../user';
import { compareDesc }                 from 'date-fns';
import { getGoogleLocationIsVerified } from '../settings/google/display_helpers';
import { getResources }                from 'redux-resource';
import { getResourceStatuses }         from '../util/get_resource_statuses';
import { getStatus }                   from 'redux-resource';
import { getStatuses }                 from '../util/get_resource_statuses';
import { parseISO }                    from 'date-fns';
import ms                              from 'ms';


// -- Business --

export function getBusiness(state, businessID) {
  return getResources(state.business, [ businessID ])[0];
}

export function getBusinessOrganization(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.organization;
}

export function getBusinessName(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.name;
}

export function getBusinessStatus(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.status;
}

export function getBusinessStatuses(state, businessID) {
  return getResourceStatuses(state.business, businessID);
}

export function getThankYouDelay(state, businessID) {
  const settings      = getSettings(state, businessID);
  const thankYouDelay = (settings?.schedule.thankYouDelay) || ms('15m');
  return thankYouDelay;
}

export function getThankYouRepeat(state, businessID) {
  const settings       = getSettings(state, businessID);
  const thankYouRepeat = (settings?.schedule.thankYouRepeat) || ms('60d');
  return thankYouRepeat;
}

export function getReadStatus(state, businessID) {
  return getStatus(state.business, `meta[${businessID}].readStatus`);
}

export function getContact(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.contact;
}

export function getSettings(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.settings;
}

export function getDefaults(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.defaults;
}

export function getRooms(state, businessID) {
  const business = getBusiness(state, businessID);
  if (business && Array.isArray(business.rooms))
    return business.rooms;
  else
    return [];
}


// -- Providers --

//  Returns an array of providers:
//
//  id      - Provider ID
//  name    - Provider name
export function getProviders(state, businessID) {
  const business = getBusiness(state, businessID);
  if (business && Array.isArray(business.users))
    return business.users.map(({ name, provider }) => ({ id: provider.id, name }));
  else
    return null;
}

export function getUsers(state, businessID) {
  const business = getBusiness(state, businessID);
  if (business && Array.isArray(business.users))
    return business.users;
  else
    return null;
}


// Returns the provider record associated with current user (may be null)
//
// id     — Provide ID
// name   - Provider name
export function getCurrentProvider(state, businessID) {
  const profile = User.getProfile(state);

  if (!profile)
    return null;

  const membership = profile.memberOf.find(({ business }) => business.id === businessID);
  const provider   = membership?.provider;
  if (provider)
    return { ...provider, name: profile.name };
  else
    return null;
}

// -- Facebook Page --

export function getFacebookPage(state, businessID) {
  const business = getBusiness(state, businessID);

  return business?.facebook?.page;
}

export function getFacebookLocationBadgeCount(state, businessID) {
  const canChangeOnlineProfiles = User.canChangeOnlineProfiles(state, businessID);
  if (!canChangeOnlineProfiles)
    return 0;

  const business = getBusiness(state, businessID);
  const facebook = business?.facebook;
  if (!facebook)
    return 0;

  const { pageID } = facebook;
  const { status } = facebook;

  const statusNeedsAction = pageID && status !== 200;

  if (statusNeedsAction)
    return 1;
  else
    return 0;
}

// -- Google Location --

export function getGoogleLocation(state, businessID) {
  const business = getBusiness(state, businessID);

  return business?.google?.location;
}

export function getGoogleLocationBadgeCount(state, businessID) {
  const canChangeOnlineProfiles = User.canChangeOnlineProfiles(state, businessID);
  if (!canChangeOnlineProfiles)
    return 0;

  const business = getBusiness(state, businessID);
  const google   = business?.google;
  if (!google)
    return 0;

  const { id }       = google;
  const { location } = google;
  const { status }   = google;

  const isConnected       = !!(id && location);
  const isVerified        = getGoogleLocationIsVerified(location);
  const statusNeedsAction = status === 403 || status === 404;

  if (!isConnected || !isVerified || statusNeedsAction)
    return 1;
  else
    return 0;
}

export function getGooglePlace(state, businessID) {
  const business = getBusiness(state, businessID);

  return business?.google?.cid && business?.google?.place;
}

// --  Yelp page

export function getYelpProfile(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.yelp;
}

// --  Nextdoor page

export function getNextdoorProfile(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.nextdoor;
}

// --  TripAdvisor page

export function getTripAdvisorProfile(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.tripAdvisor;
}

// -- Team --

// Returns information about the current team: users and providers.
export function getTeam(state, businessID) {
  const id = `${businessID}/team`;
  return getResources(state.business, [ id ])[0];
}


export function getTeamReadStatus(state, businessID) {
  const id = `${businessID}/team`;
  return getStatus(state.business, `meta[${id}].readStatus`);
}

export function getTeamStatuses(state, businessID) {
  const id = `${businessID}/team`;
  return getResourceStatuses(state.business, id);
}

// -- Data sources --

// Returns information about data sources: implicit and configured.
export function getDataSources(state, businessID) {
  const id = `${businessID}/data_sources`;
  return getResources(state.business, id);
}


export function getDataSourcesReadStatus(state, businessID) {
  const id = `${businessID}/data_sources`;
  return getResourceStatuses(state.business, id);
}


// Returns information about a specific data source
export function getDataSource(state, businessID, sourceName) {
  const id = `${businessID}/data_sources/${sourceName}`;
  return getResources(state.business, [ id ])[0];
}


export function getDataSourceReadStatus(state, businessID, sourceName) {
  const id = `${businessID}/data_sources/${sourceName}`;
  return getResourceStatuses(state.business, id);
}


// -- Upload --
//
export function getCSVMapping(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.csvMapping ?? null;
}


// -- Scorecard uses this --

export function getClassified(state, businessID) {
  const id       = `${businessID}/classified`;
  const resource = getResources(state.business, [ id ])[0];
  return resource?.classified;
}

export function getClassifiedReadStatus(state, businessID) {
  const id = `${businessID}/classified`;
  return getStatus(state.business, `meta[${id}].readStatus`);
}

export function getClassifiedStatuses(state, businessID) {
  const id = `${businessID}/classified`;

  return getStatuses(state.business, id);
}


export function getClassifiedWithMatchReview(state, businessID) {
  const classified = getClassified(state, businessID);
  if (!classified)
    return null;

  return classified.map(c => ({
    ...c,
    review: getLastReviewOfCustomer(state, businessID, c.customer.id)
  }));
}


// -- Reviews --

export function getReviews(state, businessID) {
  const id = `${businessID}/reviews`;
  return getResources(state.business, [ id ])[0];
}

export function getReviewsStatuses(state, businessID) {
  const id = `${businessID}/reviews`;

  return getStatuses(state.business, id);
}

function getLastReviewOfCustomer(state, businessID, customerID) {
  const resource = getReviews(state, businessID);
  if (!resource)
    return null;

  const { reviews } = resource;
  return reviews
    .sort((a, b) => compareDesc(parseISO(a.published), parseISO(b.published)))
    .find(r => r.customer?.id === customerID);
}



// -- Feedback --

export function getFeedback(state, businessID) {
  const id       = `${businessID}/feedback`;
  const resource = getResources(state.business, [ id ])[0];
  if (resource) {
    const { feedback } = resource;
    return feedback;
  } else
    return null;
}

export function getFeedbackStats(state, businessID, days) {
  const id = `${businessID}/feedback/stats/days-${days}`;
  return getResources(state.business, [ id ])[0];
}

export function getFeedbackStatuses(state, businessID) {
  return getStatuses(state.business, `${businessID}/feedback`);
}

export function getFeedbackStatsStatuses(state, businessID, days) {
  return getStatuses(state.business, `${businessID}/feedback/stats/days-${days}`);
}


// -- Calls --

export function getTwilioNumber(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.settings?.twilio?.number ?? null;
}

export function getHostedNumber(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.settings?.hostedNumber ?? null;
}


export function getConnectedPhoneNumber(state, businessID) {
  const hostedNumber = getHostedNumber(state, businessID);
  if (isHostedNumberComplete(state, businessID))
    return hostedNumber.number;
  else
    return getTwilioNumber(state, businessID);
}

export function isHostedNumberComplete(state, businessID) {
  const hostedNumber = getHostedNumber(state, businessID);
  return !!(hostedNumber?.number && hostedNumber.status === 'completed');
}

// -- Payments --

export function getPaymentsSettings(state, businessID) {
  const business = getBusiness(state, businessID);

  const available   = business?.features?.payments;
  const enabled     = business?.settings?.payments?.enabled;
  const tipsEnabled = business?.settings?.payments?.tips;
  const liveMode    = business?.settings?.payments?.mode === 'live';
  const fees        = business?.settings?.payments?.fees;
  const stripeURL   = `https://webapp.broadly.com/stripe/connect/${businessID}`;

  return {
    available,
    enabled,
    tipsEnabled,
    liveMode,
    stripeURL,
    fees
  };
}

// -- Classify --

export function getClassifySetting(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.settings?.classify;
}

// -- Onboarding --

// Onboarding

export function getOnboarding(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.onboarding;
}


export function needsOnboarding(state, businessID) {
  const onboarding = getOnboarding(state, businessID);
  if (!onboarding)
    return null;

  const { status } = onboarding;
  return (status === 'pending');
}


export function getBannerImage(state, businessID) {
  const business = getBusiness(state, businessID);
  return business?.bannerImageURL;
}
